import React from 'react';
import { PropTypes } from 'prop-types';
import { Card, CardContent, Typography } from '@mui/material';
import Chart from 'react-apexcharts';
import { ColorUtil } from 'utils';

// Presentational ApexCharts wrapper used across the Usage Analytics tabs.
// Supports line / area / bar, plus stacked and horizontal variants.
// Optional `action` renders on the right of the title row (e.g. a toggle);
// optional `subtitle` renders a secondary line under the title (e.g. a stat).
export function UsageOverTimeChart({
  title, subtitle, action, categories, series, type, stacked, horizontal, height, valueFormatter, integer,
}) {
  const chartType = type || 'line';
  const isArea = chartType === 'area';

  // Format numbers on the value axis (and tooltip). Non-numbers pass through
  // untouched — e.g. category labels that land on the value-axis formatter for
  // horizontal bars. Defaults to integer rounding to match prior behavior.
  const formatValue = (val) => {
    if (typeof val !== 'number') return val;
    if (valueFormatter) return valueFormatter(val);
    return Math.round(val);
  };
  const passThrough = (val) => (typeof val === 'number' ? Math.round(val) : val);

  // Largest value on the value axis so integer metrics with a tiny range
  // (e.g. a single active user) get whole-number ticks, not 0.2 / 0.4 / …
  let maxVal = 0;
  const valueOf = (d) => (d && typeof d === 'object' ? d.y : d);
  if (stacked) {
    const len = (series || []).reduce((m, s) => Math.max(m, (s.data || []).length), 0);
    for (let i = 0; i < len; i += 1) {
      let sum = 0;
      (series || []).forEach((s) => { const v = valueOf((s.data || [])[i]); if (typeof v === 'number') sum += v; });
      maxVal = Math.max(maxVal, sum);
    }
  } else {
    (series || []).forEach((s) => (s.data || []).forEach((d) => {
      const v = valueOf(d);
      if (typeof v === 'number') maxVal = Math.max(maxVal, v);
    }));
  }
  const intTicks = integer && maxVal > 0 && maxVal <= 8 ? Math.ceil(maxVal) : undefined;

  const valueLabels = { minWidth: 40, formatter: formatValue };
  const valueAxis = integer
    ? { min: 0, tickAmount: intTicks, labels: valueLabels }
    : { labels: valueLabels };

  return (
    <Card style={{ marginBottom: 16 }}>
      <CardContent>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Typography component='h2' variant='h6' color='primary'>
            {title}
          </Typography>
          <div style={{ flex: 1 }} />
          {action}
        </div>
        {subtitle && (
          <Typography variant='body2' color='textSecondary' style={{ marginBottom: 4 }}>
            {subtitle}
          </Typography>
        )}
        <Chart
          type={chartType}
          height={height || 340}
          options={{
            chart: {
              id: String(title).replace(/\s+/g, '_'),
              toolbar: { show: false },
              animations: { enabled: false },
              stacked: !!stacked,
            },
            plotOptions: {
              bar: { horizontal: !!horizontal, borderRadius: 3, columnWidth: '68%' },
            },
            fill: isArea
              ? { type: 'gradient', gradient: { opacityFrom: 0.45, opacityTo: 0.05 } }
              : { opacity: 1 },
            xaxis: horizontal
              ? {
                categories,
                min: integer ? 0 : undefined,
                tickAmount: intTicks,
                labels: valueLabels,
              }
              : {
                categories,
                labels: {
                  rotate: -45,
                  rotateAlways: false,
                  hideOverlappingLabels: true,
                },
                tickPlacement: 'on',
              },
            yaxis: horizontal
              ? { labels: { formatter: passThrough } }
              : valueAxis,
            stroke: chartType === 'bar' ? { show: false } : { curve: 'smooth', width: 2 },
            dataLabels: { enabled: false },
            legend: { position: 'top' },
            tooltip: { shared: true, intersect: false, y: { formatter: formatValue } },
            colors: ColorUtil.getColors(Math.max(series.length, 1)),
          }}
          series={series}
        />
      </CardContent>
    </Card>
  );
}

UsageOverTimeChart.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.node,
  action: PropTypes.node,
  categories: PropTypes.array.isRequired,
  series: PropTypes.array.isRequired,
  type: PropTypes.string,
  stacked: PropTypes.bool,
  horizontal: PropTypes.bool,
  height: PropTypes.number,
  valueFormatter: PropTypes.func,
  integer: PropTypes.bool,
};
